// =============================================================
// Web module table — binds the core ModuleManifest contract to web routes +
// the PermModule that gates each one. The Shell renders nav from here and
// hides entries the signed-in principal can't see (RBAC card-tab visibility).
// =============================================================

import { createRegistry, type ModuleManifest, type PermModule } from "@perpetuum/core";

export interface WebModule {
  manifest: ModuleManifest;
  /** SPA path. */
  path: string;
  /** RBAC module gating visibility/access. */
  perm: PermModule;
  /** i18n key for the nav label. */
  labelKey: string;
  /** Icon name in components/primitives ICONS. */
  icon: string;
  /**
   * Per-tenant feature-flag module id that gates this entry (Phase 9.1 toggles).
   * When set, the Shell hides the nav entry + the route is inert if the tenant
   * has the flag OFF. Mirrors the server's `measurements`/`downtimes` toggle.
   */
  featureFlag?: string;
}

export const WEB_MODULES: WebModule[] = [
  {
    manifest: {
      id: "dashboard",
      name: "Reports & dashboard",
      defaultEnabled: true,
      requiredPermissions: ["reports.read"],
      routes: [{ id: "dashboard", path: "/dashboard" }],
      nav: [{ routeId: "dashboard", labelKey: "nav.dashboard", group: "OVERVIEW" }],
    },
    path: "/dashboard",
    perm: "reports",
    labelKey: "dashboard.title",
    icon: "gauge",
  },
  {
    manifest: {
      id: "tree",
      name: "Location & device tree",
      defaultEnabled: true,
      requiredPermissions: ["dev.read"],
      routes: [{ id: "tree", path: "/tree" }],
      nav: [{ routeId: "tree", labelKey: "nav.tree", group: "OVERVIEW" }],
    },
    path: "/tree",
    perm: "dev",
    labelKey: "tree.title",
    icon: "tree",
  },
  {
    manifest: {
      id: "tasks",
      name: "Work board",
      defaultEnabled: true,
      requiredPermissions: ["tasks.read"],
      routes: [{ id: "workboard", path: "/workboard" }],
      nav: [{ routeId: "workboard", labelKey: "nav.tasks", group: "PLANNING" }],
    },
    path: "/workboard",
    perm: "tasks",
    labelKey: "workboard.title",
    icon: "wrench",
  },
  {
    manifest: {
      id: "tasks-register",
      name: "Tasks register",
      defaultEnabled: true,
      requiredPermissions: ["tasks.read"],
      routes: [{ id: "tasks-register", path: "/tasks-register" }],
      nav: [{ routeId: "tasks-register", labelKey: "register.title", group: "PLANNING" }],
    },
    path: "/tasks-register",
    perm: "tasks",
    labelKey: "register.title",
    icon: "list",
  },
  {
    manifest: {
      id: "scheduler",
      name: "PM scheduler",
      defaultEnabled: true,
      requiredPermissions: ["tasks.read"],
      routes: [{ id: "scheduler", path: "/scheduler" }],
      nav: [{ routeId: "scheduler", labelKey: "nav.scheduler", group: "PLANNING" }],
    },
    path: "/scheduler",
    perm: "tasks",
    labelKey: "scheduler.title",
    icon: "calendar",
  },
  {
    manifest: {
      id: "spares",
      name: "Spares inventory",
      defaultEnabled: true,
      requiredPermissions: ["spares.read"],
      routes: [{ id: "inventory", path: "/inventory" }],
      nav: [{ routeId: "inventory", labelKey: "nav.inventory", group: "PLANNING" }],
    },
    path: "/inventory",
    perm: "spares",
    labelKey: "inventory.title",
    icon: "box",
  },
  {
    manifest: {
      id: "downtimes",
      name: "Downtime register",
      defaultEnabled: true,
      requiredPermissions: ["downtime.read"],
      routes: [{ id: "downtimes", path: "/downtimes" }],
      nav: [{ routeId: "downtimes", labelKey: "nav.downtimes", group: "PLANNING" }],
    },
    path: "/downtimes",
    perm: "downtime",
    labelKey: "downtimes.title",
    icon: "down",
  },
  // ---- Measurements cluster (Wave 7) — gated by the `measurements` toggle ----
  {
    manifest: {
      id: "measurement-templates",
      name: "Measurement templates",
      defaultEnabled: true,
      requiredPermissions: ["dev.read"],
      routes: [{ id: "measurement-templates", path: "/measurement-templates" }],
      nav: [{ routeId: "measurement-templates", labelKey: "nav.measTemplates", group: "PLANNING" }],
    },
    path: "/measurement-templates",
    perm: "dev",
    labelKey: "measTpl.title",
    icon: "gauge",
    featureFlag: "measurements",
  },
  {
    manifest: {
      id: "meters",
      name: "Meter readings",
      defaultEnabled: true,
      requiredPermissions: ["dev.read"],
      routes: [{ id: "meters", path: "/meters" }],
      nav: [{ routeId: "meters", labelKey: "nav.meters", group: "PLANNING" }],
    },
    path: "/meters",
    perm: "dev",
    labelKey: "meters.title",
    icon: "gauge",
    featureFlag: "measurements",
  },
  {
    manifest: {
      id: "reports",
      name: "Reports",
      defaultEnabled: true,
      requiredPermissions: ["reports.read"],
      routes: [{ id: "reports", path: "/reports" }],
      nav: [{ routeId: "reports", labelKey: "nav.reports", group: "OVERVIEW" }],
    },
    path: "/reports",
    perm: "reports",
    labelKey: "reports.title",
    icon: "dashboard",
  },
  // ---- Admin / config cluster (Phase 9.1) — all gated on the admin module ----
  {
    manifest: {
      id: "settings",
      name: "Settings & dictionaries",
      defaultEnabled: true,
      requiredPermissions: ["admin.read"],
      routes: [{ id: "settings", path: "/settings" }],
      nav: [{ routeId: "settings", labelKey: "nav.settings", group: "ADMIN" }],
    },
    path: "/settings",
    perm: "admin",
    labelKey: "settings.title",
    icon: "sliders",
  },
  {
    manifest: {
      id: "access",
      name: "People & access",
      defaultEnabled: true,
      requiredPermissions: ["admin.read"],
      routes: [{ id: "access", path: "/access" }],
      nav: [{ routeId: "access", labelKey: "nav.access", group: "ADMIN" }],
    },
    path: "/access",
    perm: "admin",
    labelKey: "access.title",
    icon: "users",
  },
  {
    manifest: {
      id: "executors",
      name: "Executors & teams",
      defaultEnabled: true,
      requiredPermissions: ["admin.read"],
      routes: [{ id: "executors", path: "/executors" }],
      nav: [{ routeId: "executors", labelKey: "executors.title", group: "ADMIN" }],
    },
    path: "/executors",
    perm: "admin",
    labelKey: "executors.title",
    icon: "users",
  },
  {
    manifest: {
      id: "groups",
      name: "User groups",
      defaultEnabled: true,
      requiredPermissions: ["admin.read"],
      routes: [{ id: "groups", path: "/groups" }],
      nav: [{ routeId: "groups", labelKey: "groups.title", group: "ADMIN" }],
    },
    path: "/groups",
    perm: "admin",
    labelKey: "groups.title",
    icon: "shield",
  },
  {
    manifest: {
      id: "admin",
      name: "Admin console",
      defaultEnabled: true,
      requiredPermissions: ["admin.read"],
      routes: [{ id: "admin", path: "/admin" }],
      nav: [{ routeId: "admin", labelKey: "nav.admin", group: "ADMIN" }],
    },
    path: "/admin",
    perm: "admin",
    labelKey: "admin.title",
    icon: "shield",
  },
  // ---- Review Center (Phase 9.3) — parked offline mutations (sync) ----
  {
    manifest: {
      id: "review",
      name: "Review Center",
      defaultEnabled: true,
      requiredPermissions: ["tasks.read"],
      routes: [{ id: "review", path: "/review" }],
      nav: [{ routeId: "review", labelKey: "nav.review", group: "ADMIN" }],
    },
    path: "/review",
    perm: "tasks",
    labelKey: "review.title",
    icon: "inbox",
  },
];

/** The core registry, populated from the web module manifests (validated). */
export const registry = createRegistry(WEB_MODULES.map((m) => m.manifest));
